import React from 'react';

/* ─── ShimmerLoader ────────────────────────────────────────────────────────────
 * Countertop logo on the cream backdrop with a soft "spotlight" oscillating
 * left ↔ right inside the logo silhouette plus a gentle whole-logo brightness
 * pulse — visually matches the mobile SplashLogo in WrappedWebScreen.js and
 * the static-HTML shimmer at public/shimmer-loader.css.
 *
 * Each band is mask-image-clipped to the logo silhouette, then renders a
 * horizontally-shifting bright gradient on its ::before. Three bands of
 * progressively narrower gradients stack to a soft Gaussian-ish falloff.
 *
 * Props:
 *   compact  {boolean}  Use a smaller logo (140px vs 200px) for section-level
 *                       loading states embedded within an already-loaded page.
 *   style    {object}   Additional styles applied to the outer wrapper.
 *
 * Usage:
 *   import ShimmerLoader from './ShimmerLoader';
 *   // Full-region (fills parent):
 *   <ShimmerLoader />
 *   // Compact inline section:
 *   <ShimmerLoader compact />
 * ──────────────────────────────────────────────────────────────────────────── */

const BG = '#FAF6F2';

// Class names use a `-r` suffix to avoid colliding with the static-HTML
// loader's `.ct-shimmer-*` classes when both stylesheets are present.
const STYLES = `
.ct-shimmer-wrap-r {
  position: relative;
  flex-shrink: 0;
  isolation: isolate;
  animation: ct-shimmer-pulse-r 3600ms ease-in-out infinite alternate;
}

.ct-shimmer-img-r {
  width: 100%;
  height: auto;
  display: block;
}

@keyframes ct-shimmer-pulse-r {
  from { transform: scale(0.997); }
  to   { transform: scale(1.012); }
}

.ct-shimmer-band-r {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
  -webkit-mask-image: url('/icons/Countertop-app_logo_text_only_counter.png');
          mask-image: url('/icons/Countertop-app_logo_text_only_counter.png');
  -webkit-mask-size: 100% 100%;
          mask-size: 100% 100%;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  mix-blend-mode: screen;
}

.ct-shimmer-band-r::before {
  content: '';
  position: absolute;
  inset: 0;
  background-size: 200% 100%;
  background-repeat: no-repeat;
  background-position: 100% 50%;
  animation: ct-shimmer-spot-r 4800ms ease-in-out infinite alternate;
}

@keyframes ct-shimmer-spot-r {
  from { background-position: 100% 50%; }
  to   { background-position: 0% 50%; }
}

@keyframes ct-shimmer-breathe-r {
  from { opacity: 0.65; }
  to   { opacity: 1; }
}

.ct-shimmer-band-r-1::before {
  background-image: linear-gradient(
    90deg,
    rgba(255,250,235,0)    0%,
    rgba(255,250,235,0.22) 25%,
    rgba(255,250,235,0.30) 50%,
    rgba(255,250,235,0.22) 75%,
    rgba(255,250,235,0)    100%
  );
}

.ct-shimmer-band-r-2::before {
  background-image: linear-gradient(
    90deg,
    rgba(255,250,235,0)    20%,
    rgba(255,250,235,0.38) 50%,
    rgba(255,250,235,0)    80%
  );
}

.ct-shimmer-band-r-3::before {
  background-image: linear-gradient(
    90deg,
    rgba(255,250,235,0)    37%,
    rgba(255,250,235,0.55) 50%,
    rgba(255,250,235,0)    63%
  );
  animation: ct-shimmer-spot-r 4800ms ease-in-out infinite alternate,
             ct-shimmer-breathe-r 1800ms ease-in-out infinite alternate;
}

@media (prefers-reduced-motion: reduce) {
  .ct-shimmer-band-r::before { animation: none !important; }
  .ct-shimmer-wrap-r { animation: none !important; }
}
`;

let _styleInjected = false;
function injectStyles() {
  if (_styleInjected || typeof document === 'undefined') return;
  _styleInjected = true;
  const el = document.createElement('style');
  el.id = 'ct-shimmer-keyframes';
  el.textContent = STYLES;
  document.head.appendChild(el);
}

export default function ShimmerLoader({ compact = false, style = {} }) {
  injectStyles();

  const logoSize = compact ? 140 : 200;

  const outerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: compact ? 180 : 280,
    background: BG,
    ...style,
  };

  const wrapStyle = {
    width: logoSize,
    maxWidth: '55vw',
  };

  return (
    <div style={outerStyle} aria-label="Loading" role="status">
      <div className="ct-shimmer-wrap-r" style={wrapStyle}>
        <img
          src="/icons/Countertop-app_logo_text_only_counter.png"
          alt="Countertop"
          className="ct-shimmer-img-r"
          aria-hidden="true"
        />
        <div className="ct-shimmer-band-r ct-shimmer-band-r-1" />
        <div className="ct-shimmer-band-r ct-shimmer-band-r-2" />
        <div className="ct-shimmer-band-r ct-shimmer-band-r-3" />
      </div>
    </div>
  );
}
